"use client";

import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Script from "next/script";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ScrollConsent, type PrivacyConsentHandle } from "@/features/service-information/PrivacyConsent";
import { PrivacyPolicyBody } from "@/features/service-information/PrivacyPolicyBody";
import { FACE_CONSENT_TEXT, FACE_CONSENT_VERSION, type PreparedFaceIdentity } from "../face-consent";
import { queryReturnedFace } from "../face-return-query";

declare global { interface Window { getMetaInfo?: () => unknown } }
const pendingMessage = "认证结果正在同步，请勿重复刷脸；稍后点击“查询认证结果”。";
const fieldClass = "rounded-lg border border-[var(--color-line)] bg-[var(--color-sunken)] px-3 py-2";

export function FaceVerificationForm({ nonce, showBackLink = true, onboarding = false, handoff = false, handoffToken, returning = false, onVerified, preview = false, onPrepare, preparedIdentity, onBack }: { onBack?: () => void; onPrepare?: (identity: PreparedFaceIdentity) => Promise<void>; preparedIdentity?: PreparedFaceIdentity; preview?: boolean; nonce?: string; showBackLink?: boolean; onboarding?: boolean; handoff?: boolean; handoffToken?: string; returning?: boolean; onVerified?: () => void }) {
  const router = useRouter();
  const [status, setStatus] = useState<{ enabled: boolean; passed: boolean; identityPrepared?: boolean } | null>(preview ? { enabled: true, passed: false } : null);
  const [consent, setConsent] = useState(Boolean(preparedIdentity?.consent));
  const handoffPrepared = Boolean(handoffToken && status?.identityPrepared && !onPrepare);
  const consentGiven = consent || handoffPrepared;
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [handoffExpired, setHandoffExpired] = useState(false);
  const [message, setMessage] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [scriptFailed, setScriptFailed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const autoStarted = useRef(false);
  const autoStart = handoffPrepared && !returning && !preview;
  const consentControl = useRef<PrivacyConsentHandle>(null);
  const verifiedCallback = useRef(onVerified);
  const preparing = Boolean(onPrepare);
  const endpoint = handoffToken ? "/api/auth/face/handoff/verify" : "/api/auth/face";
  const send = useCallback(async (body: object) => {
    const response = await fetch(endpoint, { method: "POST", ...(handoffToken ? { credentials: "omit" as const } : {}), headers: { "Content-Type": "application/json", ...(handoffToken ? { Authorization: `Bearer ${handoffToken}` } : {}) }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok) { if (data.code === "HANDOFF_EXPIRED") setHandoffExpired(true); if (data.code === "REAUTH_REQUIRED") setReauthRequired(true); throw new Error(data.error || "认证服务暂时不可用"); }
    return data;
  }, [endpoint, handoffToken]);
  useEffect(() => { verifiedCallback.current = onVerified; }, [onVerified]);
  useEffect(() => {
    if (preparing && status?.enabled && !status.passed) formRef.current?.querySelector<HTMLInputElement>('input[name="realName"]')?.focus();
  }, [preparing, status]);
  useEffect(() => {
    if (preview) return;
    let active = true;
    const controller = new AbortController();
    // Browser-return query parameters are not proof and are never sent to the API.
    if (window.location.pathname === "/verify/face" && window.location.search) window.history.replaceState(null, "", "/verify/face");
    if (onboarding && returning) window.history.replaceState(null, "", handoff ? "/onboarding/face" : "/onboarding");
    fetch(endpoint, { cache: "no-store", ...(handoffToken ? { credentials: "omit" as const, headers: { Authorization: `Bearer ${handoffToken}` } } : {}) }).then(async response => {
      const data = await response.json();
      if (!response.ok) { if (active && data.code === "HANDOFF_EXPIRED") setHandoffExpired(true); if (active && data.code === "REAUTH_REQUIRED") setReauthRequired(true); throw new Error(data.error || "无法读取认证状态"); }
      if (!active) return;
      setStatus(data);
      if (data.passed) { verifiedCallback.current?.(); return; }
      if (returning) {
        setBusy(true);
        const result = await queryReturnedFace(() => send({ action: "check" }), controller.signal, () => setMessage("认证结果正在同步，请稍候，无需重复刷脸。"));
        if (!active) return;
        if (result.passed) { setMessage(""); setStatus({ enabled: data.enabled, passed: true }); verifiedCallback.current?.(); }
        else setMessage(result.status === "failed" ? "此次刷脸认证未通过，请核对本人资料后重新发起。" : pendingMessage);
      }
    }).catch(error => { if (active) { setLoadFailed(true); setMessage(error instanceof Error ? error.message : "无法读取认证状态"); } })
      .finally(() => { if (active) setBusy(false); });
    // The provider's return parameters only trigger a server query, never approval.
    // Requests completed after unmount must not update the next page.
    return () => { active = false; controller.abort(); };
  }, [onboarding, handoff, handoffToken, endpoint, returning, loadAttempt, send, preview]);
  useEffect(() => {
    if (!autoStart || !status?.enabled || status.passed || !ready || busy || scriptFailed
      || handoffExpired || reauthRequired || loadFailed || autoStarted.current || !formRef.current) return;
    // Start once after the server validates the QR identity and the device SDK is ready.
    // Provider returns only query the result; failed starts require an explicit retry.
    autoStarted.current = true;
    formRef.current.requestSubmit();
  }, [autoStart, status, ready, busy, scriptFailed, handoffExpired, reauthRequired, loadFailed]);
  async function reauthenticate() {
    if (busy) return;
    setBusy(true);
    try {
      const response = await fetch("/api/auth/logout", { method: "POST" });
      if (!response.ok) throw new Error("暂时无法重新登录，请稍后再试。");
      const next = onboarding ? handoff ? "/onboarding/face" : "/onboarding" : "/verify/face";
      // Never persist names, ID numbers or consent across authentication.
      router.replace(`/login?next=${encodeURIComponent(next)}`);
      router.refresh();
    } catch (error) { setMessage(error instanceof Error ? error.message : "暂时无法重新登录"); setBusy(false); }
  }
  async function start(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preview) { setMessage("此入口不发起认证，请通过上方状态切换继续。"); return; }
    if (busy || !consentGiven || (!onPrepare && (!ready || !window.getMetaInfo))) return;
    const form = event.currentTarget;
    setBusy(true); setMessage("");
    try {
      const fields = new FormData(form);
      if (onPrepare) {
        await onPrepare({ realName: String(fields.get("realName") || ""), idCard: String(fields.get("idCard") || ""), consent: true, consentVersion: FACE_CONSENT_VERSION });
        return;
      }
      const meta = window.getMetaInfo!();
      const metaInfo = typeof meta === "string" ? meta : JSON.stringify(meta);
      const data = await send(handoffPrepared ? { action: "start", metaInfo } : { action: "start", realName: fields.get("realName"), idCard: fields.get("idCard"),
        metaInfo, consent: true, consentVersion: FACE_CONSENT_VERSION,
        ...(onboarding ? { context: handoff ? "onboarding-handoff" : "onboarding" } : {}) });
      form.reset();
      if (onboarding) window.dispatchEvent(new Event("mira-registration-face-handoff"));
      window.location.assign(data.certifyUrl);
    } catch (error) { setMessage(error instanceof Error ? error.message : "无法启动认证"); }
    finally { setBusy(false); }
  }
  async function check() {
    if (preview) { setMessage("此入口没有认证结果。"); return; }
    setBusy(true); setMessage("");
    try {
      const data = await send({ action: "check" });
      if (data.passed) { setStatus(current => ({ enabled: Boolean(current?.enabled), passed: true })); verifiedCallback.current?.(); }
      else if (data.status === "failed") { setConsent(false); setMessage("此次刷脸认证未通过，请核对本人资料后重新发起；已用次数不退回。"); }
      else setMessage(pendingMessage);
    } catch (error) { setMessage(error instanceof Error ? error.message : "无法查询认证结果"); }
    finally { setBusy(false); }
  }
  return <div className="grid max-w-xl gap-5">
    {status?.passed ? <p className="card-surface rounded-[var(--radius-card)] p-5 text-[var(--color-verified)]">刷脸实名认证已通过</p>
      : handoffExpired ? <p role="alert">请返回原入驻页面扫描最新二维码。</p> : reauthRequired ? <button type="button" disabled={busy} onClick={() => void reauthenticate()} className="btn-silver w-fit rounded-full px-4 py-2 disabled:opacity-50">{busy ? "正在前往登录…" : "重新登录并继续认证"}</button> : status?.enabled ? <>
        {!onboarding && !onPrepare && (!handoffToken || returning) && <button type="button" disabled={busy} onClick={check} className="chip w-fit rounded-full px-4 py-2 disabled:opacity-50">{busy ? "处理中…" : "查询认证结果"}</button>}
        <form ref={formRef} onSubmit={start} className="card-surface grid gap-4 rounded-[var(--radius-card)] p-5">
          {!handoffPrepared && <>
          <label className="grid gap-1 text-sm">姓名<input required name="realName" minLength={2} maxLength={60} autoComplete="name" defaultValue={preparedIdentity?.realName} className={fieldClass} /></label>
          <label className="grid gap-1 text-sm">身份证<input required name="idCard" minLength={18} maxLength={18} autoComplete="off" defaultValue={preparedIdentity?.idCard} className={fieldClass} /></label>
          <ScrollConsent ref={consentControl} agreed={consent} onChange={setConsent} title="人脸信息处理说明" separate>
            <p className="text-sm leading-7">{FACE_CONSENT_TEXT}</p>
            <h3 className="mt-6 font-medium">隐私政策</h3>
            <PrivacyPolicyBody />
          </ScrollConsent>
          </>}
          {consentGiven && !preview && !onPrepare && <Script src="https://o.alicdn.com/yd-cloudauth/cloudauth-cdn/jsvm_all.js" nonce={nonce} strategy="afterInteractive"
            onReady={() => { const available = typeof window.getMetaInfo === "function"; setReady(available); setScriptFailed(!available); }} onError={() => setScriptFailed(true)} />}
          {scriptFailed ? <><p role="alert" className="text-sm">认证组件加载失败，请重新加载页面后重试。</p><button type="button" className="chip w-fit rounded-full px-4 py-2" onClick={() => window.location.reload()}>重新加载页面</button></> : autoStart && !message ? <p role="status">正在启动刷脸认证…</p> : <>
            <GuidedSubmitButton type="submit" disabled={busy} reason={!consentGiven ? "请阅读并同意人脸信息处理说明。" : !ready && !preview && !onPrepare ? "正在加载认证组件，请稍候。" : ""}
              onBlocked={() => { if (!consentGiven) consentControl.current?.requestConsent(() => {}); }}
              className={onboarding ? "rg-primary" : "btn-silver w-fit rounded-full px-4 py-2 disabled:opacity-50"}>{busy ? "处理中…" : autoStart ? "重新启动刷脸认证" : onPrepare && !handoff ? "下一步" : onboarding ? "下一步" : "开始刷脸认证"}</GuidedSubmitButton>
          </>}
        </form>
      </> : status ? <p className="card-surface rounded-[var(--radius-card)] p-5">{onboarding ? "刷脸认证暂不可用，请稍后重试或联系平台处理。" : "刷脸认证暂未开放，你仍可提交人工核验申请。"}</p> : loadFailed ? <button type="button" className="chip w-fit rounded-full px-4 py-2" onClick={() => { setLoadFailed(false); setMessage(""); setLoadAttempt(value => value + 1); }}>重新读取认证状态</button> : <p>正在读取认证状态…</p>}
    {message && <p role={message.startsWith("认证结果正在同步") ? "status" : "alert"} className={message.startsWith("认证结果正在同步") ? "text-sm" : "text-sm text-red-500"}>{message}</p>}
    {onBack && !status?.passed && !handoffExpired && !reauthRequired && <button type="button" className="rg-secondary min-h-11" disabled={busy} onClick={onBack}>返回上一步</button>}
    {showBackLink && <Link href="/verify" className="text-sm underline">返回实名认证</Link>}
  </div>;
}
